/**
 * AEGIS prepaid cart guard
 * ------------------------------------------------------------------
 * Welcome Gifts ($0) belongs to the prepaid Refill Pouch:
 *   - Exactly ONE Welcome Gifts line, qty 1, whenever a prepaid pouch is in the cart
 *     (whatever the pouch quantity).
 *   - Last prepaid pouch removed -> Welcome Gifts removed IN THE SAME REQUEST
 *     (the outgoing /cart/change or /cart/update is rewritten), so the cart page
 *     and the Recharge drawer update in one round trip.
 *   - Welcome Gifts on its own -> removed.  Missing while pouch present -> re-added.
 *
 * Config: window.AegisPrepaidConfig (layout/theme.liquid).
 */
import { CartUpdateEvent } from '@theme/events';

const cfg = window.AegisPrepaidConfig || {};
const GIFTS = Number(cfg.giftsVariantId) || 0;
const POUCH = Number(cfg.pouchVariantId) || 0;
const PLAN = Number(cfg.prepaidPlanId) || 0;
const READD_GIFTS = cfg.readdGifts !== false;
const MAX_PREPAID_QTY = Number(cfg.maxPrepaidQty) || 3; // max prepaid pouches per cart (client: 3)
const GUARD_HEADER = 'X-Aegis-Cart-Guard';
const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

if (GIFTS && POUCH && PLAN && !window.__aegisPrepaidGuard) {
  window.__aegisPrepaidGuard = true;

  const nativeFetch = window.fetch.bind(window);
  const pageFetch = window.fetch; // may already be wrapped by Recharge — keep it in the chain
  let lastCart = null; // latest known cart, used to rewrite outgoing requests
  let running = null;
  let queued = false;
  let dirty = true; // cart changed and not yet verified

  const isPrepaidPouch = (i) => i.variant_id === POUCH && i.selling_plan_allocation?.selling_plan?.id === PLAN;
  const isGift = (i) => i.variant_id === GIFTS;
  const isCart = (c) => c && Array.isArray(c.items) && 'item_count' in c;

  /* ---------------- helpers ---------------- */

  // Form data, not JSON: Recharge's fetch wrapper fails on JSON bodies.
  function toFormData(body) {
    const fd = new FormData();
    Object.entries(body.updates || {}).forEach(([key, qty]) => fd.append(`updates[${key}]`, String(qty)));
    (body.items || []).forEach((item, i) => {
      fd.append(`items[${i}][id]`, String(item.id));
      fd.append(`items[${i}][quantity]`, String(item.quantity || 1));
      Object.entries(item.properties || {}).forEach(([k, v]) => fd.append(`items[${i}][properties][${k}]`, String(v)));
    });
    return fd;
  }

  async function guardRequest(url, body) {
    // Through window.fetch so the Recharge drawer also sees it; header prevents a loop.
    const res = await window.fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json', [GUARD_HEADER]: '1' },
      body: toFormData(body),
    });
    const text = await res.text();
    try {
      return text ? JSON.parse(text) : null;
    } catch (e) {
      return null;
    }
  }

  async function fetchCart() {
    const cart = await nativeFetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    lastCart = cart;
    return cart;
  }

  /* ---------------- enforce ---------------- */

  async function enforce(knownCart) {
    const cart = isCart(knownCart) ? knownCart : await fetchCart();
    lastCart = cart;

    const pouches = cart.items.filter(isPrepaidPouch);
    const hasPrepaid = pouches.length > 0;
    const gifts = cart.items.filter(isGift);
    const wanted = hasPrepaid ? 1 : 0;

    const updates = {};
    // Prepaid pouch: cap total quantity
    let left = MAX_PREPAID_QTY;
    if (MAX_PREPAID_QTY) pouches.forEach((line) => {
      const q = Math.min(line.quantity, left);
      left -= q;
      if (q !== line.quantity) updates[line.key] = q;
    });
    // Welcome Gifts: one line, qty 1 while prepaid is present
    gifts.forEach((line, idx) => {
      const q = idx === 0 ? wanted : 0;
      if (q !== line.quantity) updates[line.key] = q;
    });
    const needsGifts = hasPrepaid && !gifts.length && READD_GIFTS;

    if (!Object.keys(updates).length && !needsGifts) return;

    let result = null;
    if (Object.keys(updates).length) {
      result = await guardRequest('/cart/update.js', { updates });
    }
    if (needsGifts) {
      result = null;
      await guardRequest('/cart/add.js', {
        items: [{ id: GIFTS, quantity: 1, properties: { _aegis_welcome_gifts: 'prepaid' } }],
      });
    }

    const fresh = isCart(result) ? result : await fetchCart();
    lastCart = fresh;
    document.dispatchEvent(
      new CartUpdateEvent(fresh, 'aegis-prepaid-guard', {
        source: 'aegis-prepaid-guard',
        // Welcome Gifts is hidden in the cart UI, so leave it out of the count
        itemCount: fresh.item_count - fresh.items.filter(isGift).reduce((n, i) => n + i.quantity, 0),
      })
    );
  }

  function check(knownCart) {
    if (running) {
      queued = true;
      return running;
    }
    running = enforce(knownCart)
      .then(() => {
        if (!queued) dirty = false;
      })
      .catch((e) => console.warn('[aegis-prepaid-guard]', e))
      .finally(() => {
        running = null;
        if (queued) {
          queued = false;
          check();
        }
      });
    return running;
  }
  window.AegisPrepaidGuard = { check };

  /**
   * Re-read the cart once things settle after a cart change. The Recharge cart
   * drawer listens to /cart.js requests and redraws from the response, so this
   * makes sure it never keeps showing a stale subtotal (e.g. $121 instead of $112
   * right after an add, while Recharge is still making its own follow-up request).
   */
  let syncTimer = null;
  function syncDrawer() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      Promise.resolve(running)
        .catch(() => {})
        .then(() =>
          window.fetch('/cart.js', { headers: { Accept: 'application/json', [GUARD_HEADER]: '1' } })
        )
        .catch(() => {});
    }, 600);
  }

  /* ---------------- request rewriting (instant removal) ---------------- */

  function readBody(body) {
    if (!body) return null;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const obj = {};
      for (const [k, v] of body.entries()) obj[k] = v;
      return { kind: body instanceof FormData ? 'form' : 'params', obj };
    }
    if (typeof body === 'string') {
      try {
        return { kind: 'json', obj: JSON.parse(body) };
      } catch (e) {
        return { kind: 'params', obj: Object.fromEntries(new URLSearchParams(body)) };
      }
    }
    return null;
  }

  function findLine(cart, ref) {
    if (ref.line != null && ref.line !== '') return cart.items[Number(ref.line) - 1];
    const id = String(ref.id ?? '');
    return cart.items.find((i) => i.key === id) || cart.items.find((i) => String(i.variant_id) === id);
  }

  /**
   * If this request leaves no prepaid pouch in the cart, rewrite it into one
   * /cart/update request that also removes Welcome Gifts. Otherwise null.
   */
  function rewriteForGifts(url, init) {
    if (!lastCart || !init || !init.body) return null;
    const gifts = lastCart.items.filter(isGift);
    if (!gifts.length) return null;

    const parsed = readBody(init.body);
    if (!parsed) return null;
    const o = parsed.obj;

    const newQty = {};
    if (/\/cart\/change/.test(url)) {
      const line = findLine(lastCart, { line: o.line, id: o.id });
      if (!line) return null;
      newQty[line.key] = Number(o.quantity ?? 1);
    } else if (/\/cart\/update/.test(url)) {
      if (Array.isArray(o.updates)) {
        o.updates.forEach((qty, idx) => lastCart.items[idx] && (newQty[lastCart.items[idx].key] = Number(qty)));
      } else {
        const ups = { ...(o.updates && typeof o.updates === 'object' ? o.updates : {}) };
        Object.keys(o).forEach((k) => {
          const m = k.match(/^updates\[(.+)\]$/);
          if (m) ups[m[1]] = o[k];
        });
        Object.entries(ups).forEach(([ref, qty]) => {
          const line = findLine(lastCart, { id: ref });
          if (line) newQty[line.key] = Number(qty);
        });
      }
    } else {
      return null;
    }

    const touchesPrepaid = lastCart.items.some((i) => isPrepaidPouch(i) && i.key in newQty);
    const prepaidLeft = lastCart.items
      .filter(isPrepaidPouch)
      .reduce((n, i) => n + (i.key in newQty ? newQty[i.key] : i.quantity), 0);
    if (!touchesPrepaid || prepaidLeft > 0) return null;

    gifts.forEach((g) => (newQty[g.key] = 0));

    // Keep the caller's extras (sections etc.) so its own response handling still works.
    const extras = {};
    ['sections', 'sections_url'].forEach((k) => o[k] != null && (extras[k] = o[k]));
    const newUrl = url.replace(/\/cart\/(change|update)(\.js)?/, '/cart/update.js');

    let body;
    if (parsed.kind === 'json') {
      body = JSON.stringify({ updates: newQty, ...extras });
    } else {
      const fd = parsed.kind === 'form' ? new FormData() : new URLSearchParams();
      Object.entries(newQty).forEach(([k, q]) => fd.append(`updates[${k}]`, String(q)));
      Object.entries(extras).forEach(([k, v]) => fd.append(k, v));
      body = fd;
    }
    return { url: newUrl, init: { ...init, body } };
  }

  /* ---------------- watch fetch / XHR ---------------- */

  function isGuardRequest(init) {
    const h = init && init.headers;
    if (!h) return false;
    if (h instanceof Headers) return h.has(GUARD_HEADER);
    return Object.keys(h).some((k) => k.toLowerCase() === GUARD_HEADER.toLowerCase());
  }

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url ? input.url : String(input);
    let args = arguments;

    if (CART_MUTATION.test(url) && !isGuardRequest(init)) {
      dirty = true;
      try {
        const rw = typeof input === 'string' ? rewriteForGifts(url, init) : null;
        if (rw) args = [rw.url, rw.init];
      } catch (e) {
        console.warn('[aegis-prepaid-guard] rewrite skipped', e);
      }
      const p = pageFetch.apply(this, args);
      p.then((res) =>
        res
          .clone()
          .json()
          .then((data) => check(isCart(data) ? data : null))
          .catch(() => check())
          .finally(syncDrawer)
      ).catch(() => {});
      return p;
    }

    const p = pageFetch.apply(this, args);
    // Keep the snapshot fresh whenever anything reads the cart.
    if (/\/cart(\.js)?(\?|$)/.test(url)) {
      p.then((res) => res.clone().json().then((c) => isCart(c) && (lastCart = c))).catch(() => {});
    }
    return p;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_MUTATION.test(String(url))) {
      dirty = true;
      this.addEventListener('loadend', () => {
        check();
        syncDrawer();
      });
    }
    return xhrOpen.apply(this, arguments);
  };

  /* ---------------- before checkout ---------------- */

  document.addEventListener(
    'click',
    (event) => {
      const path = event.composedPath ? event.composedPath() : [event.target];
      const el = path.find(
        (n) =>
          n instanceof Element &&
          (n.matches('button[name="checkout"], a[href*="/checkout"], [data-checkout], form[action*="/checkout"] button') ||
            (n.matches('button, a') && /^\s*(secure\s+)?check\s?out\b/i.test(n.textContent || '')))
      );
      if (!el || el.dataset.aegisGuardPassed || !dirty) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      check().finally(() => {
        el.dataset.aegisGuardPassed = '1';
        el.click();
        setTimeout(() => delete el.dataset.aegisGuardPassed, 0);
      });
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!/\/cart\/?$/.test(form.getAttribute('action') || '') || form.dataset.aegisGuardPassed || !dirty) return;
      event.preventDefault();
      check().finally(() => {
        form.dataset.aegisGuardPassed = '1';
        form.requestSubmit ? form.requestSubmit(event.submitter || undefined) : form.submit();
      });
    },
    true
  );

  check();
  window.addEventListener('pageshow', (e) => e.persisted && check());
}